<template>
  <div class="accordion" :class="{ 'accordion--columns': columns }">
    <div v-for="(item, index) in items" :key="item.id ?? index" class="accordion__item">
      <h3 class="accordion__heading">
        <button
          :id="`${uid}-trigger-${index}`"
          class="accordion__trigger"
          type="button"
          :aria-expanded="isOpen(index) ? 'true' : 'false'"
          :aria-controls="`${uid}-panel-${index}`"
          @click="toggle(index)"
        >
          <span class="accordion__question">{{ item.question }}</span>
          <span class="accordion__chevron" :class="{ 'is-open': isOpen(index) }" aria-hidden="true"></span>
        </button>
      </h3>
      <div
        :id="`${uid}-panel-${index}`"
        class="accordion__panel"
        role="region"
        :aria-labelledby="`${uid}-trigger-${index}`"
        :hidden="!isOpen(index)"
      >
        <div class="accordion__answer">
          <slot name="answer" :item="item" :index="index">
            <p>{{ item.answer }}</p>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { nextUid } from '@/utils/uid';

/*
 * Did not exist anywhere in the codebase. The FAQ rendered all ten questions
 * permanently expanded — roughly ten screens of prose with nothing to scan —
 * with no button, no aria-expanded and no keyboard affordance.
 */
const props = defineProps({
  items: { type: Array, required: true },
  /* Multiple panels open at once; a FAQ is a scanning surface, not a wizard. */
  single: Boolean,
  defaultOpen: { type: Array, default: () => [] },
  /*
   * Questions are short. In one column each closed row was ~850px of card
   * holding ~30 characters, so the list read as mostly empty and pushed the
   * page past two screens. Rows size independently (align-items: start), so
   * opening one panel does not stretch its neighbour.
   */
  columns: Boolean,
});

const uid = nextUid('accordion');
const open = ref(new Set(props.defaultOpen));

const isOpen = (index) => open.value.has(index);

function toggle(index) {
  const next = new Set(props.single && !open.value.has(index) ? [] : open.value);
  if (next.has(index)) {
    next.delete(index);
  } else {
    next.add(index);
  }
  open.value = next;
}
</script>

<style scoped>
.accordion {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

@media (min-width: 48rem) {
  .accordion--columns {
    display: grid;
    /* Fixed at two rather than auto-fit: auto-fit gave a four-item group three
     * columns and a ragged orphan on the second row. */
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: var(--space-4);
    align-items: start;
  }
}

.accordion__item {
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  transition: border-color var(--duration) var(--ease);
}

.accordion__item:has(.accordion__trigger[aria-expanded='true']) {
  border-color: var(--border-accent);
}

.accordion__heading {
  margin: 0;
  font-size: inherit;
}

.accordion__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  width: 100%;
  padding: var(--space-4) var(--space-5);
  background: none;
  border: 0;
  cursor: pointer;
  text-align: left;
  color: var(--text);
  font-family: var(--font-body);
  font-size: var(--text-lg);
  font-weight: var(--weight-semibold);
  line-height: var(--leading-snug);
  transition: color var(--duration) var(--ease);
}

.accordion__trigger:hover {
  color: var(--accent);
}

.accordion__trigger[aria-expanded='true'] {
  color: var(--accent);
}

.accordion__chevron {
  flex: none;
  width: 0.6rem;
  height: 0.6rem;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(45deg);
  transform-origin: center;
  transition: transform var(--duration) var(--ease);
  margin-bottom: 0.25rem;
}

.accordion__chevron.is-open {
  transform: rotate(-135deg);
  margin-bottom: -0.15rem;
}

.accordion__answer {
  padding: 0 var(--space-5) var(--space-5);
  max-width: var(--measure);
  color: var(--text-muted);
  line-height: var(--leading-body);
}

.accordion__answer :deep(p + p) {
  margin-top: var(--space-3);
}

.accordion__answer :deep(a) {
  color: var(--accent);
  text-decoration: underline;
  text-decoration-color: var(--accent-faint);
  text-underline-offset: 0.2em;
  font-style: normal;
}

.accordion__answer :deep(ul) {
  padding-left: 1.35em;
  margin-top: var(--space-3);
}

.accordion__answer :deep(li)::marker {
  color: var(--accent-dim);
}
</style>
